/**
 * Binary-searches the largest font-size that fits each element in its container,
 * then applies the minimum across all elements so they align.
 *
 * @param {Element[]} elements  - Text elements to fit (block-level by default)
 * @param {object}   options
 * @param {number}   options.maxFontSize - Upper bound for binary search (default 1000)
 * @param {number}   options.cap         - Hard cap on the resulting font-size (default Infinity)
 * @param {boolean}  options.inline      - Set true for inline/inline-flex elements (e.g. stat numbers)
 *                                         Compares against parentElement instead of the element itself.
 * @returns {number} The applied font-size in px (0 if no elements)
 */
export function fitText(elements, { maxFontSize = 1000, cap = Infinity, inline = false } = {}) {
  const sizes = [];

  elements.forEach((el) => {
    const parent = el.parentElement;
    if (!parent) return;

    el.style.whiteSpace = "nowrap";
    if (inline) el.style.display = "inline-flex";

    let lo = 1, hi = maxFontSize;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      el.style.fontSize = mid + "px";
      const overflowing = inline
        ? el.scrollWidth > parent.clientWidth || el.scrollHeight > parent.clientHeight
        : el.scrollWidth > el.clientWidth;
      if (overflowing) hi = mid - 1;
      else lo = mid;
    }

    sizes.push(lo);
  });

  if (!sizes.length) return 0;

  const min = Math.min(Math.min(...sizes), cap);
  elements.forEach((el) => {
    el.style.fontSize = min + "px";
  });

  return min;
}
